import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { useQuery } from '@tanstack/react-query';
import { SiteHeader } from '@/components/layout/SiteHeader';
import { SiteFooter } from '@/components/layout/SiteFooter';
import { CurrencySwitcher } from '@/components/layout/CurrencySwitcher';
import { billingApi } from '@/lib/billing';
import { useDocumentMeta } from '@/lib/useDocumentMeta';
import {
  currentDisplayCurrency,
  fromUsd,
  formatDisplayCurrency,
} from '@/lib/displayCurrency';

interface Plan {
  key: 'free' | 'growth' | 'global' | 'enterprise';
  name: string;
  monthlyUsdPerSeat: number;
  perks: string[];
  cta: string;
  ctaTo: string;
  highlight: boolean;
}

export function PricingPage() {
  const { t, i18n } = useTranslation();
  const [currency, setCurrency] = useState(currentDisplayCurrency());
  useDocumentMeta({
    title: 'Pricing — Knowlez',
    description: 'Public per-seat pricing for Knowlez. Free, Growth ($24), Global ($59), Enterprise ($129). Cloud-first with on-prem option at Enterprise.',
    path: '/pricing',
  });

  // Stripe-sourced live plan catalogue. Single source of truth — a
  // Stripe Dashboard price edit propagates to this page within the
  // backend cache TTL (~10 min) with zero code changes.
  const plansQ = useQuery({ queryKey: ['billing-plans'], queryFn: () => billingApi.plans(), staleTime: 5 * 60 * 1000 });
  const priceFor = (code: 'free' | 'growth' | 'global' | 'enterprise'): number => {
    const p = plansQ.data?.find((x) => x.code === code);
    return p ? p.monthlyUnitAmountCents / 100 : 0;
  };

  const plans: Plan[] = [
    {
      key: 'free',
      name: t('pricing.free', { defaultValue: 'Free' }) as string,
      monthlyUsdPerSeat: priceFor('free'),
      perks: [
        t('pricing.free.seats', { defaultValue: 'Up to 10 seats' }) as string,
        t('pricing.free.connectors', { defaultValue: '5 connectors' }) as string,
        t('pricing.free.chunks', { defaultValue: '100,000 indexed chunks' }) as string,
        t('pricing.free.agents', { defaultValue: 'All 6 agents (bring your own AI key)' }) as string,
      ],
      cta: t('pricing.cta.signup', { defaultValue: 'Start free' }) as string,
      ctaTo: '/signup',
      highlight: false,
    },
    {
      key: 'growth',
      name: t('pricing.growth', { defaultValue: 'Growth' }) as string,
      monthlyUsdPerSeat: priceFor('growth'),
      perks: [
        t('pricing.growth.connectors', { defaultValue: 'Unlimited connectors' }) as string,
        t('pricing.growth.chunks', { defaultValue: '5M indexed chunks' }) as string,
        t('pricing.growth.cap', { defaultValue: 'Included AI cap (~$2 / user / month)' }) as string,
        t('pricing.growth.support', { defaultValue: 'Email support, <24h business-day SLA' }) as string,
      ],
      cta: t('pricing.cta.startGrowth', { defaultValue: 'Start Growth' }) as string,
      ctaTo: '/signup?plan=growth',
      highlight: true,
    },
    {
      key: 'global',
      name: t('pricing.global', { defaultValue: 'Global' }) as string,
      monthlyUsdPerSeat: priceFor('global'),
      perks: [
        t('pricing.global.region', { defaultValue: 'Multi-region indexing (Tokyo · EU · US)' }) as string,
        t('pricing.global.priority', { defaultValue: 'Priority indexing queue' }) as string,
        t('pricing.global.kg', { defaultValue: 'Knowledge graph + entity resolution (JP 法人番号 + global domain)' }) as string,
        t('pricing.global.signals', { defaultValue: 'Signal alerts + Slack / Teams / LINE WORKS / Chatwork delivery' }) as string,
      ],
      cta: t('pricing.cta.startGlobal', { defaultValue: 'Start Global' }) as string,
      ctaTo: '/signup?plan=global',
      highlight: false,
    },
    {
      key: 'enterprise',
      name: t('pricing.enterprise', { defaultValue: 'Enterprise' }) as string,
      monthlyUsdPerSeat: priceFor('enterprise'),
      perks: [
        t('pricing.enterprise.sso', { defaultValue: 'SSO/SAML/OIDC + SCIM v2' }) as string,
        t('pricing.enterprise.audit', { defaultValue: 'Audit log + 7-year retention' }) as string,
        t('pricing.enterprise.onprem', { defaultValue: 'On-prem option (no license premium)' }) as string,
        t('pricing.enterprise.sla', { defaultValue: '99.95% SLA · dedicated CSM' }) as string,
      ],
      cta: t('pricing.cta.tryEnterprise', { defaultValue: 'Try Enterprise' }) as string,
      ctaTo: '/signup?plan=enterprise',
      highlight: false,
    },
  ];

  return (
    <div className="min-h-screen paper-archive">
      <SiteHeader />
      <main className="mx-auto max-w-6xl px-5 sm:px-6 pt-16 pb-24">
        <div className="flex items-start justify-between flex-wrap gap-4">
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="max-w-2xl"
          >
            <p className="text-[11px] uppercase tracking-[0.18em] text-accent-600 font-semibold">
              {t('pricing.eyebrow', { defaultValue: 'Pricing' })}
            </p>
            <h1 className="mt-3 display serif-jp text-4xl md:text-5xl font-semibold tracking-tight text-ink-900 leading-[1.05] text-balance">
              {t('pricing.title', { defaultValue: 'Public pricing. No contact-sales.' })}
            </h1>
            <p className="mt-4 text-[16px] text-ink-600 leading-relaxed text-pretty">
              {t('pricing.subtitle', {
                defaultValue:
                  'Predictable per-seat pricing. No seat minimums, no annual lock-in. Cancel anytime. Annual discounts available, never required.',
              })}
            </p>
          </motion.div>
          <CurrencySwitcher onChange={setCurrency} />
        </div>

        <div className="mt-12 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {plans.map((p, i) => (
            <PlanCard key={p.key} plan={p} displayCurrency={currency} locale={i18n.language} t={t} index={i} />
          ))}
        </div>

        <PricingCalculator plans={plans} displayCurrency={currency} locale={i18n.language} t={t} />

        <p className="mt-10 text-[12px] text-ink-500 max-w-3xl leading-relaxed">
          {t('pricing.footnote', {
            defaultValue:
              'Prices contracted in USD. Local-currency amounts are illustrative, updated when we revise the price page. AI usage is billed at cost on top of seat fees (typical workloads ~$1.50–$9 per seat per month depending on the model tier and traffic). Workspaces can cap monthly AI spend.',
          })}
        </p>
      </main>
      <SiteFooter />
    </div>
  );
}

function PlanCard({
  plan,
  displayCurrency,
  locale,
  t,
  index,
}: {
  plan: Plan;
  displayCurrency: string;
  locale: string;
  t: (k: string, opts?: Record<string, unknown>) => string;
  index: number;
}) {
  const converted = fromUsd(plan.monthlyUsdPerSeat, displayCurrency);
  const priceLabel =
    plan.monthlyUsdPerSeat === 0
      ? formatDisplayCurrency(0, displayCurrency, locale)
      : formatDisplayCurrency(converted.amount, displayCurrency, locale);

  return (
    <motion.div
      initial={{ opacity: 0, y: 14 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.1 + index * 0.06, duration: 0.5, ease: [0.16, 0.84, 0.44, 1] }}
      className={`card-paper relative p-6 flex flex-col ${
        plan.highlight ? 'ring-1 ring-accent-500/40 -mt-2 md:-mt-3 shadow-card-lift' : ''
      }`}
    >
      {plan.highlight && (
        <span className="absolute top-0 right-4 -translate-y-1/2 inline-flex text-[10px] uppercase tracking-[0.18em] font-semibold bg-accent-600 text-white rounded-full px-2.5 py-1">
          {t('pricing.popular', { defaultValue: 'Most popular' })}
        </span>
      )}
      <h3 className="display text-[19px] font-semibold text-ink-900">{plan.name}</h3>
      <p className="mt-3 num text-3xl font-semibold text-ink-900">{priceLabel}</p>
      <p className="text-[11.5px] text-ink-500 mt-1">
        {plan.monthlyUsdPerSeat === 0
          ? t('pricing.unit.free', { defaultValue: 'forever · up to 10 seats' })
          : t('pricing.unit.paid', { defaultValue: 'per seat · per month · billed monthly' })}
      </p>
      <hr className="my-5 border-ink-100" />
      <ul className="space-y-2 text-[13.5px] text-ink-700 flex-1">
        {plan.perks.map((perk) => (
          <li key={perk} className="flex gap-2.5 items-start leading-relaxed">
            <Check />
            <span>{perk}</span>
          </li>
        ))}
      </ul>
      <Link
        to={plan.ctaTo}
        className={
          plan.highlight
            ? 'btn-press mt-6 w-full justify-center'
            : 'btn-ghost mt-6 w-full justify-center'
        }
      >
        {plan.cta}
      </Link>
    </motion.div>
  );
}

function PricingCalculator({
  plans, displayCurrency, locale, t,
}: {
  plans: Plan[];
  displayCurrency: string;
  locale: string;
  t: (k: string, opts?: Record<string, unknown>) => string;
}) {
  const [seats, setSeats] = useState(20);
  const [annual, setAnnual] = useState(false);

  const paid = plans.filter((p) => p.monthlyUsdPerSeat > 0);
  const ANNUAL_DISCOUNT = 0.15;

  return (
    <section className="mt-16 card-paper p-7 md:p-9">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="text-[11px] uppercase tracking-[0.18em] text-accent-600 font-semibold">
            {t('pricing.calc.eyebrow', { defaultValue: 'Live calculator' })}
          </p>
          <h2 className="mt-2 display text-2xl md:text-3xl font-semibold text-ink-900 tracking-tight">
            {t('pricing.calc.title', { defaultValue: 'What does your team actually pay?' })}
          </h2>
          <p className="mt-2 text-[13.5px] text-ink-600 leading-relaxed max-w-2xl">
            {t('pricing.calc.subtitle', {
              defaultValue:
                'No 100-seat minimum, no annual lock-in. Drag the slider and see exact cost — the number we charge you, not a "starting from."',
            })}
          </p>
        </div>
        <label className="inline-flex items-center gap-2 cursor-pointer select-none">
          <span className="text-[12.5px] text-ink-600">{t('pricing.calc.annual', { defaultValue: 'Annual (-15%)' })}</span>
          <span className="relative inline-flex h-5 w-9">
            <input
              type="checkbox"
              checked={annual}
              onChange={(e) => setAnnual(e.target.checked)}
              className="sr-only peer"
            />
            <span className="absolute inset-0 rounded-full bg-ink-200 peer-checked:bg-accent-500 transition-colors" />
            <span className="absolute top-0.5 left-0.5 h-4 w-4 rounded-full bg-white shadow transition-transform peer-checked:translate-x-4" />
          </span>
        </label>
      </div>

      <div className="mt-6">
        <div className="flex items-baseline justify-between gap-4">
          <label htmlFor="seats" className="text-[13px] text-ink-700 font-medium">
            {t('pricing.calc.seats_label', { defaultValue: 'Seats' })}
          </label>
          <div className="num text-2xl font-semibold text-ink-900 tabular-nums">
            {seats.toLocaleString()}
          </div>
        </div>
        <input
          id="seats"
          type="range"
          min={1}
          max={500}
          step={1}
          value={seats}
          onChange={(e) => setSeats(Number(e.target.value))}
          className="mt-3 w-full h-2 rounded-full appearance-none bg-ink-100 outline-none accent-accent-500"
          aria-label={t('pricing.calc.seats_label', { defaultValue: 'Seats' }) as string}
        />
        <div className="mt-1.5 flex justify-between text-[10.5px] text-ink-400 num">
          <span>1</span>
          <span>50</span>
          <span>100</span>
          <span>200</span>
          <span>500+</span>
        </div>
      </div>

      <div className="mt-7 grid grid-cols-1 md:grid-cols-3 gap-3">
        {paid.map((p) => {
          const monthlyUsd = p.monthlyUsdPerSeat * seats;
          const billUsd = annual ? monthlyUsd * 12 * (1 - ANNUAL_DISCOUNT) : monthlyUsd;
          const conv = fromUsd(billUsd, displayCurrency);
          const display = formatDisplayCurrency(conv.amount, displayCurrency, locale);
          const cadence = annual
            ? t('pricing.calc.per_year', { defaultValue: '/ year' })
            : t('pricing.calc.per_month', { defaultValue: '/ month' });
          return (
            <div
              key={p.key}
              className={`rounded-xl border ${p.highlight ? 'border-accent-500/40 bg-accent-50/40' : 'border-ink-100 bg-paper-50'} p-5`}
            >
              <div className="flex items-center justify-between">
                <span className="display text-[14.5px] font-semibold text-ink-900">{p.name}</span>
                {p.highlight && (
                  <span className="text-[10px] uppercase tracking-[0.14em] font-semibold text-accent-600">
                    {t('pricing.popular', { defaultValue: 'Most popular' })}
                  </span>
                )}
              </div>
              <div className="mt-2 num text-2xl font-semibold text-ink-900 tabular-nums">
                {display}
              </div>
              <div className="text-[11.5px] text-ink-500 num">
                {cadence} · {seats} × ${p.monthlyUsdPerSeat}
                {annual ? ` × 12 × ${(1 - ANNUAL_DISCOUNT).toFixed(2)}` : ''}
              </div>
              {p.key === 'growth' && (
                <p className="mt-3 text-[11.5px] text-emerald-700 leading-relaxed">
                  {t('pricing.calc.vs_glean', {
                    defaultValue: 'vs Glean (~$50 / seat, 100-seat min): you save ~{{save}}%',
                    save: Math.round((1 - 24 / 50) * 100),
                  })}
                </p>
              )}
            </div>
          );
        })}
      </div>

      <p className="mt-5 text-[11px] text-ink-400 leading-relaxed">
        {t('pricing.calc.foot', {
          defaultValue: 'AI usage billed at cost (zero markup) on top. Customers cap monthly AI spend per workspace.',
        })}
      </p>
    </section>
  );
}

function Check() {
  return (
    <svg viewBox="0 0 24 24" className="h-4 w-4 mt-0.5 shrink-0 text-accent-600" fill="none" stroke="currentColor" strokeWidth="2.5">
      <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
    </svg>
  );
}
